import React from 'react';
import {
  CardBody,
  CardHeader,
  Label,
  FormGroup,
  Form,
  Input,
  Button,
} from 'reactstrap';
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useHistory } from 'react-router-dom';
import { actionSubmitExtRefDoctor } from 'store/actions/usersActions';

/**
 * @component DoctorUsersEditExtRef
 * @description Form component to edit external references for a doctor's associated practices.
 * Iterates safely over the hydrated `practices` array from the Redux store, avoiding 
 * undefined mapping errors. Maps flat form values back into a structured array on submit.
 */
const DoctorUsersEditExtRef = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  
  const userData = useSelector(state => state.userDoctorsReducer.editData);
  const { register, handleSubmit } = useForm();

  /**
   * Transforms the flat form data back into an array of objects
   * containing the practice ID and the updated external reference.
   * * @param {Object} data - Flat form data from react-hook-form.
   */
  const onSubmit = (data) => {
    const refinedData = Object.entries(data).reduce((acc, [k, v]) => {
      const keysplit = k.split("_"); 
      const id = keysplit[keysplit.length - 1]; 
      const keyvalue = keysplit.slice(0, keysplit.length - 1).join("_"); 
      
      if (!acc[id]) {
        acc[id] = {};
      }
      
      acc[id][keyvalue] = v; 
      return acc;
    }, []);

    dispatch(actionSubmitExtRefDoctor({ ...userData, set_ext_refs: refinedData }));
  };

  const practices = Array.isArray(userData?.practices) ? userData.practices : [];
  const fullName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim();

  if (practices.length === 0) {
    return (
      <>
        <CardHeader>{`${fullName} External Reference Edition`}</CardHeader>
        <CardBody>
          <p className="text-muted">No practices associated with this user.</p>
        </CardBody>
      </>
    );
  }

  return (
    <>
      <CardHeader>{`${fullName} External Reference Edition`}</CardHeader>
      <CardBody>
        <Form id="user-edit-ext-ref" onSubmit={handleSubmit(onSubmit)}>
          {practices.map((practice, index) => {
            const practiceName = practice.name || practice.practice_name || `Practice ${index + 1}`;
            const practiceId = practice.practiceId || practice._id || '';

            return (
              <React.Fragment key={index}>
                <Label>External Reference ({practiceName})</Label>
                <FormGroup>
                  <Input
                    type="text"
                    name={`ext_ref_${index}`}
                    defaultValue={practice.doctor_ext_ref || ''}
                    required
                    innerRef={register}
                  />
                  <Input
                    type="hidden"
                    name={`practice_${index}`}
                    defaultValue={practiceId}
                    innerRef={register}
                  />
                </FormGroup>
              </React.Fragment>
            );
          })}
        </Form>

        <Button form="user-edit-ext-ref" size="sm" color="info" type="submit" className="mr-2">
          Update
        </Button>

        <Button
          size="sm"
          color="danger"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            history.goBack();
          }}
        >
          Cancel
        </Button>
      </CardBody>
    </>
  );
};

export default DoctorUsersEditExtRef;